import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';
import { useGetOrMintLicense } from './useGetOrMintLicense';

export type SessionType = 'interview' | 'online_assessment';

export interface ScheduleSessionInput {
  endClientId: string;
  sessionType: SessionType;
  companyName: string;
  positionTitle: string;
  interviewDate: string; // ISO
  interviewNotes: string;
  /** Pre-uploaded material ids (resume, JD, notes). The wizard runs upload + parse on step 2 before reaching Confirm. */
  materialIds: string[];
}

export interface ScheduleSessionResult {
  sessionId: string;
  licenseId: string;
  licenseKey: string;
  promptId: string | null;
  emailWarning?: string;
}

async function waitForMaterialsParsed(
  materialIds: string[],
  opts: { timeoutMs?: number; intervalMs?: number } = {}
): Promise<void> {
  const timeoutMs = opts.timeoutMs ?? 60_000;
  const intervalMs = opts.intervalMs ?? 2_000;
  if (materialIds.length === 0) return;

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const { data, error } = await supabase
      .schema('neurax' as never)
      .from('end_client_materials' as never)
      .select('id, file_name, content, parsed_data')
      .in('id', materialIds);
    if (error) return; // best-effort — let prompt-gen poll handle the rest
    const rows = (data ?? []) as {
      id: string;
      file_name: string | null;
      content: string | null;
      parsed_data: unknown | null;
    }[];
    const allParsed = rows.every((r) => {
      if (!r.file_name) return true; // text-only
      return Boolean(r.content) && r.parsed_data !== null;
    });
    if (allParsed && rows.length === materialIds.length) return;
    await new Promise((res) => setTimeout(res, intervalMs));
  }
}

export function useScheduleSession() {
  const qc = useQueryClient();
  const mintLicense = useGetOrMintLicense();
  const { companyId } = useBranding();

  return useMutation({
    mutationFn: async (input: ScheduleSessionInput): Promise<ScheduleSessionResult> => {
      // 1. License (reuse or mint). Non-demo flow: pass just the end client id
      // (demoOpts is optional and only used by the admin demo flow).
      const license = await mintLicense.mutateAsync({ endClientId: input.endClientId });

      // 2. Materials are uploaded by the wizard at step 2 — we just take their ids
      const materialIds = input.materialIds;

      // 3. Create session row
      const userRes = await supabase.auth.getUser();
      const setupBy = userRes.data.user?.id ?? null;

      const sessionRow: Record<string, unknown> = {
        b2b_company_id: companyId,
        b2b_end_client_id: input.endClientId,
        license_id: license.id,
        company_name: input.companyName,
        position_title: input.positionTitle,
        interview_date: input.interviewDate,
        interview_notes: input.interviewNotes || null,
        session_type: input.sessionType,
        status: 'pending',
        is_billable: true,
        is_billed: false,
        setup_completed_at: new Date().toISOString(),
      };
      if (setupBy) sessionRow.setup_by = setupBy;

      const { data: session, error: sessionErr } = await supabase
        .schema('neurax' as never)
        .from('interview_sessions' as never)
        .insert(sessionRow as never)
        .select()
        .single();

      if (sessionErr || !session) {
        throw new Error(`interview_sessions insert: ${sessionErr?.message ?? 'failed'}`);
      }
      const sessionId = (session as { id: string }).id;

      // 4. Wait for AI parsing on uploaded files to finish (defense-in-depth —
      //    the wizard already blocks Continue until parsed, so this is fast).
      await waitForMaterialsParsed(materialIds);

      // 5. Generate prompt server-side. Edge function returns prompt_content
      //    (NOT prompt_id) — we have to insert the row ourselves. Pattern mirrors
      //    client/src/lib/b2b/useEndClientPrompts.ts:260-300.
      let promptId: string | null = null;
      try {
        const { data: gen, error: promptErr } = await supabase.functions.invoke(
          'generate-interview-prompt',
          {
            body: {
              end_client_id: input.endClientId,
              material_ids: materialIds,
              session_type: input.sessionType,
            },
          }
        );
        if (promptErr) throw new Error(promptErr.message);

        const promptContent = (gen as { prompt_content?: string } | null)?.prompt_content;
        const suggestedName = (gen as { suggested_name?: string } | null)?.suggested_name;
        const modelUsed = (gen as { model_used?: string } | null)?.model_used;

        if (promptContent) {
          const { data: promptRow, error: insertPromptErr } = await supabase
            .schema('neurax' as never)
            .from('end_client_prompts' as never)
            .insert({
              b2b_end_client_id: input.endClientId,
              // REQUIRED for B2B RLS — see existing portal pattern at
              // client/src/lib/b2b/useEndClientPrompts.ts:130-131
              b2b_company_id: companyId,
              name: suggestedName || `Interview Prep — ${input.positionTitle || 'session'}`,
              description: 'Auto-generated by NeuraX Portal scheduler',
              prompt_content: promptContent,
              is_auto_generated: true,
              is_active: true,
              is_default: true,
              generation_source: {
                material_ids: materialIds,
                model_used: modelUsed || 'gemini-2.5-flash',
                generated_at: new Date().toISOString(),
              },
            } as never)
            .select()
            .single();
          if (insertPromptErr) throw new Error(`end_client_prompts: ${insertPromptErr.message}`);
          promptId = (promptRow as { id?: string } | null)?.id ?? null;
        }
      } catch (err) {
        console.warn('generate-interview-prompt / save failed (continuing):', err);
      }

      // 5. Patch session.prompt_id (if we got one)
      if (promptId) {
        await supabase
          .schema('neurax' as never)
          .from('interview_sessions' as never)
          .update({ prompt_id: promptId } as never)
          .eq('id', sessionId);
      }

      // 6. Patch license.current_interview_session_id
      const { error: licUpdErr } = await supabase
        .from('neurax_licenses')
        .update({ current_interview_session_id: sessionId } as never)
        .eq('id', license.id);
      if (licUpdErr) {
        console.warn('failed to set current_interview_session_id:', licUpdErr.message);
      }

      // NOTE: used_quota is bumped server-side by the
      // `trg_bump_b2b_used_quota` trigger on neurax.interview_sessions.
      // (B2B users can't UPDATE b2b_companies directly — RLS gates it to platform admins.)

      // 7. Send candidate email
      let emailWarning: string | undefined;
      try {
        const { error: mailErr } = await supabase.functions.invoke('send-session-scheduled', {
          body: { interview_session_id: sessionId },
        });
        if (mailErr) emailWarning = mailErr.message;
      } catch (err) {
        emailWarning = err instanceof Error ? err.message : String(err);
      }

      qc.invalidateQueries({ queryKey: ['neurax', 'interviews'] });
      qc.invalidateQueries({ queryKey: ['neurax', 'licenses'] });
      qc.invalidateQueries({ queryKey: ['neurax', 'dashboard-stats'] });
      qc.invalidateQueries({ queryKey: ['neurax', 'billing'] });

      return {
        sessionId,
        licenseId: license.id,
        licenseKey: license.license_key,
        promptId,
        ...(emailWarning ? { emailWarning } : {}),
      };
    },
  });
}
